import { useDaily, useLocalSessionId, useParticipantProperty, useRoom } from '@daily-co/daily-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  leaveRoomPresenceRedis,
  registerRoomPresenceRedis,
} from '/lib/fetchRoomPresence';
import { getQueryParam } from '/lib/query';

interface UseRoomPresenceRedisOptions {
  roomId?: string;
  roomName?: string;
  enabled?: boolean;
  registerOnMount?: boolean;
  leaveOnUnmount?: boolean;
  isOwner?: boolean;
}

export const resolvePresenceUserId = (
  query: Record<string, string | string[]>,
  participantUserId?: string
) => getQueryParam('userId', query) || participantUserId || undefined;

export const useRoomPresenceRedis = ({
  roomId,
  roomName,
  enabled = true,
  registerOnMount = false,
  leaveOnUnmount = false,
  isOwner: isOwnerProp,
}: UseRoomPresenceRedisOptions) => {
  const { query } = useRouter();
  const daily = useDaily();
  const room = useRoom();
  const localSessionId = useLocalSessionId();
  const participantUserId = useParticipantProperty(localSessionId, 'user_id');
  const registeredRef = useRef(false);
  const userId = resolvePresenceUserId(query, participantUserId);
  const isOwner = useMemo(
    () => isOwnerProp ?? Boolean(room?.tokenConfig?.is_owner),
    [isOwnerProp, room]
  );

  const register = useCallback(async () => {
    if (!enabled || !roomName || registeredRef.current) return;
    registeredRef.current = true;
    await registerRoomPresenceRedis(roomId, roomName, userId, isOwner);
  }, [enabled, isOwner, roomId, roomName, userId]);

  const leave = useCallback(async () => {
    if (!roomName || !registeredRef.current) return;
    registeredRef.current = false;
    await leaveRoomPresenceRedis(roomId, roomName, userId, { isOwner });
  }, [isOwner, roomId, roomName, userId]);

  useEffect(() => {
    if (!registerOnMount) return;
    register();
  }, [register, registerOnMount]);

  useEffect(() => {
    if (!leaveOnUnmount) return;
    return () => {
      leave();
    };
  }, [leave, leaveOnUnmount]);

  useEffect(() => {
    if (!enabled || !daily || !roomName) return;

    const handleBeforeUnload = () => {
      leaveRoomPresenceRedis(roomId, roomName, userId, {
        keepalive: true,
        isOwner,
      });
      registeredRef.current = false;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [daily, enabled, isOwner, roomId, roomName, userId]);

  return {
    userId,
    isOwner,
    register,
    leave,
  };
};
